'use client';
import React, { useRef } from 'react';
import cn from '@utils/cn';
import { GridColFullWidth, GridContainerColClasses, gridREMLayout } from '@constants/layout';
import useIsInView from '@lib/hooks/useIsInView';
import Text, { TextVariant } from '@ui/Text/Text';
import TextBlock from '@ui/Text/TextBlock';
import { Stat } from '@lib/sanity/sanity.types';
import { SanityI18nProjection } from '@lib/sanity/types/common';
import { TestimonialStatType } from '@lib/sanity/queries/modules/testimonialsStats/types';

type StatsProps = {
  data: TestimonialStatType;
};

export default function Stats({ data }: StatsProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useIsInView(containerRef);

  if (!data) {
    return null;
  }

  function renderStat(stat: SanityI18nProjection<Stat>, i: number) {
    if (!stat?.name?.value || !stat?.value) {
      return null;
    }

    return (
      <div
        key={i}
        className={cn(
          'col-span-2 flex flex-col items-center lg:col-span-3',
          'justify-center gap-2 rounded-lg p-6 text-center',
        )}
      >
        <StatValue value={stat.value} isInView={isInView} />
        <Text content={stat.name.value} variant={TextVariant.SubtitleSM} className="min-h-[3.2em]" />
      </div>
    );
  }

  return (
    <div ref={containerRef} className={cn(gridREMLayout, 'md:pt-8')}>
      <div className={cn('flex flex-col gap-8 ', GridColFullWidth, 'sm:grid-cols-4')}>
        <div className={cn(GridContainerColClasses, 'gap-8 sm:grid-cols-4')}>{data.stats?.map(renderStat)}</div>
      </div>
    </div>
  );
}

type StatsValueProps = {
  value: string;
  isInView: boolean;
};

function StatValue({ value, isInView }: StatsValueProps) {
  const number = parseInt(value, 10);
  const rest = value.replace(number.toString(), '');

  if (isNaN(number)) {
    return <Text content={value} variant={TextVariant.TextStats} as="p" />;
  }

  const className = cn(
    'tabular-nums',
    'transition-[_--num] ease-out',
    'supports-[counter-set]:before:content-[counter(num)]',
  );
  const style = {
    counterSet: 'num var(--num)',
    transitionDuration: '3s',
    '--num': isInView ? number : 0,
  } as React.CSSProperties;

  return (
    <TextBlock variant={TextVariant.TextStats} as="p" className={className} style={style}>
      <span className="supports-[counter-set]:sr-only">{number}</span>
      {rest}
    </TextBlock>
  );
}
